import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from '../../components/DatePicker';
import UsageInfo from '../../components/UsageInfo';
import { getEpostaLoglar } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import DataTable, { Rozet } from '../../components/DataTable';
import { downloadExcel, printTable } from '../../utils/exportUtils';

// ── Sabitler ──────────────────────────────────────────────────────────────
const DURUMLAR = ['Hepsi', 'Gönderildi', 'Başarısız', 'Bekliyor'];

// ── Yardımcılar ───────────────────────────────────────────────────────────
function pad(n) { return String(n).padStart(2, '0'); }
function ayBasStr() { var d=new Date(); return '01.'+pad(d.getMonth()+1)+'.'+d.getFullYear(); }
function aySonuStr() {
  var d=new Date(); var son=new Date(d.getFullYear(),d.getMonth()+1,0);
  return pad(son.getDate())+'.'+pad(d.getMonth()+1)+'.'+d.getFullYear();
}
function toIso(ddmmyyyy) {
  if (!ddmmyyyy) return '';
  var p = ddmmyyyy.split('.');
  if (p.length < 3) return '';
  return p[2]+'-'+p[1]+'-'+p[0];
}

// ── Detay Modal ───────────────────────────────────────────────────────────
function DetayModal({ kayit, onKapat }) {
  var durumRenk = {
    'Gönderildi': 'bg-emerald-100/10 text-emerald-600 border-emerald-200/20',
    'Başarısız':  'bg-red-100/10 text-red-600 border-red-200/20',
    'Bekliyor':   'bg-amber-100/10 text-amber-600 border-amber-200/20',
  };
  var durumIkon = { 'Gönderildi':'✅', 'Başarısız':'❌', 'Bekliyor':'⏳' };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 transform-gpu">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-md transition-opacity duration-300" onClick={onKapat} />
      <div className="bg-white/90 backdrop-blur-xl rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-white/40 overflow-hidden relative z-10 animate-modal-in transform-gpu">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100/50 bg-gray-50/30">
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-xl shadow-blue-100 transform-gpu hover:rotate-6 transition-transform">✉️</div>
            <div>
              <h2 className="text-lg font-black text-gray-900 tracking-tighter">E-posta Detayı</h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{kayit.tarih} — {kayit.saat}</p>
            </div>
          </div>
          <button onClick={onKapat} className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white/80 backdrop-blur-md text-gray-400 hover:bg-gray-900 hover:text-white transition-all text-xl font-bold shadow-sm border border-gray-100 transform-gpu active:scale-90">×</button>
        </div>

        {/* Modal Body */}
        <div className="px-8 py-8 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
          <div className="flex justify-between items-center bg-gray-50/50 backdrop-blur-sm p-4 rounded-[1.5rem] border border-gray-100/50 transform-gpu">
            <div className="flex items-center gap-3">
              <span className={'text-[10px] font-black px-4 py-2 rounded-full border uppercase tracking-widest shadow-sm ' + (durumRenk[kayit.durum]||'bg-gray-100 text-gray-600')}>
                {durumIkon[kayit.durum]} {kayit.durum}
              </span>
              <span className="text-[10px] font-black text-gray-400 bg-white/80 px-4 py-2 rounded-full border border-gray-100 uppercase tracking-widest shadow-sm">{kayit.tip}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label:'ALICI',  value: kayit.misafir, icon:'👤' },
              { label:'E-MAIL', value: kayit.email, icon:'📧'   },
              { label:'KONU',   value: kayit.konu, icon:'📝'    },
              { label:'İŞLEM',  value: kayit.tip, icon:'⚙️'     },
            ].map(function(row) {
              return (
                <div key={row.label} className="bg-white/50 backdrop-blur-sm p-4 rounded-2xl border border-white/40 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow transform-gpu">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-lg shadow-sm border border-gray-50">{row.icon}</div>
                  <div className="min-w-0">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.15em] mb-0.5">{row.label}</p>
                    <p className="text-xs font-bold text-gray-900 truncate">{row.value}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div>
             <div className="flex items-center gap-2 mb-3 ml-1">
               <div className="w-1.5 h-3.5 bg-blue-500 rounded-full shadow-sm shadow-blue-200"></div>
               <p className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Mesaj İçeriği</p>
             </div>
             <div className="bg-white/80 backdrop-blur-md border border-gray-50 rounded-[2rem] p-6 shadow-inner min-h-[200px] transform-gpu">
               <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed font-medium" dangerouslySetInnerHTML={{ __html: kayit.icerik?.replace(/\n/g, '<br/>') }} />
             </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-8 py-6 bg-gray-50/50 backdrop-blur-sm border-t border-gray-100/50 flex gap-3 transform-gpu">
          {kayit.durum === 'Başarısız' && (
            <button className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-black px-8 py-4 rounded-2xl shadow-xl shadow-blue-100 uppercase tracking-widest transition-all active:scale-95 transform-gpu">
              🔄 Yeniden Gönder
            </button>
          )}
          <button onClick={onKapat} className="ml-auto text-[10px] font-black px-8 py-4 rounded-2xl bg-white border-2 border-gray-100 hover:bg-gray-900 hover:text-white hover:border-gray-900 transition-all uppercase tracking-widest shadow-sm transform-gpu">
            Kapat
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Sıralama İkonu ────────────────────────────────────────────────────────
function SortIcon({ field, sortKey, sortDir }) {
  if (sortKey !== field) return <span className="text-gray-300 ml-1 text-xs transition-opacity duration-300 opacity-50">⇅</span>;
  return <span className="text-blue-500 ml-1 text-xs animate-bounce-subtle">{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

// ── Ana Sayfa ─────────────────────────────────────────────────────────────
/** E-posta durumuna gore rozet rengi. */
function durumRozet(durum) {
  var d = (durum || '').toLocaleLowerCase('tr-TR');
  if (d.includes('gönder') || d.includes('basar') || d.includes('başar')) return 'emerald';
  if (d.includes('hata') || d.includes('basarisiz') || d.includes('başarısız')) return 'rose';
  if (d.includes('bekl')) return 'amber';
  return 'gray';
}

export default function GonderilmisEmailler() {
  var [baslar,    setBaslar]    = useState(ayBasStr());
  var [bitis,     setBitis]     = useState(aySonuStr());
  var [durum,     setDurum]     = useState('Hepsi');
  var [aktifBas,  setAktifBas]  = useState(ayBasStr());
  var [aktifBit,  setAktifBit]  = useState(aySonuStr());
  var [aktifDurum,setAktifDurum]= useState('Hepsi');
  var [colTarih,  setColTarih]  = useState('');
  var [colEmail,  setColEmail]  = useState('');
  var [colKonu,   setColKonu]   = useState('');
  var [colDurum,  setColDurum]  = useState('');
  var [sortKey,   setSortKey]   = useState('tarih');
  var [sortDir,   setSortDir]   = useState('desc');
  var [detay,     setDetay]     = useState(null);
  var [liste,     setListe]     = useState([]);
  var [yukleniyor,setYukleniyor]= useState(false);
  var [hata,      setHata]      = useState(null);

  var yukle = useCallback(function() {
    setYukleniyor(true);
    setHata(null);
    getEpostaLoglar({
      baslangic:   toIso(aktifBas),
      bitis:       toIso(aktifBit),
      durum:       aktifDurum !== 'Hepsi' ? aktifDurum : undefined,
      sayfaBoyutu: 500,
    }).then(function(data) {
      setListe(data?.satirlar || []);
    }).catch(function() {
      setHata('Veriler yüklenemedi.');
    }).finally(function() {
      setYukleniyor(false);
    });
  }, [aktifBas, aktifBit, aktifDurum]);

  useEffect(function() { yukle(); }, [yukle]);

  function filtrele() { setAktifBas(baslar); setAktifBit(bitis); setAktifDurum(durum); }
  function temizle() {
    setBaslar(ayBasStr()); setBitis(aySonuStr()); setDurum('Hepsi');
    setAktifBas(ayBasStr()); setAktifBit(aySonuStr()); setAktifDurum('Hepsi');
    setColTarih(''); setColEmail(''); setColKonu(''); setColDurum('');
  }
  function handleSort(key) {
    if (sortKey === key) setSortDir(function(d){ return d==='asc'?'desc':'asc'; });
    else { setSortKey(key); setSortDir('desc'); }
  }

  function excelIndir() {
    var baslik = ['Tarih','Saat','Misafir','E-mail','Konu','Tip','Durum'];
    var satirlar = goruntulenen.map(function(e) {
      return [e.tarih, e.saat, e.misafir, e.email, e.konu, e.tip, e.durum];
    });
    downloadExcel('eposta_gecmisi', 'Gönderilmiş E-postalar', baslik, satirlar);
  }

  function yazdir() {
    var baslik = ['Tarih','Saat','Misafir','E-mail','Konu','Tip','Durum'];
    var satirlar = goruntulenen.map(function(e) { return [e.tarih, e.saat, e.misafir, e.email, e.konu, e.tip, e.durum]; });
    printTable('Gönderilmiş E-postalar', baslik, satirlar, goruntulenen.length + ' e-posta');
  }

  var goruntulenen = useMemo(function() {
    return liste.filter(function(e) {
      if (colTarih && !(e.tarih+' '+e.saat).includes(colTarih)) return false;
      if (colEmail && !e.email.toLowerCase().includes(colEmail.toLowerCase())) return false;
      if (colKonu  && !e.konu.toLocaleLowerCase('tr-TR').includes(colKonu.toLocaleLowerCase('tr-TR'))) return false;
      if (colDurum && !e.durum.toLocaleLowerCase('tr-TR').includes(colDurum.toLocaleLowerCase('tr-TR'))) return false;
      return true;
    }).sort(function(a, b) {
      var ak = sortKey === 'tarih' ? a.tarih+' '+a.saat : a[sortKey]||'';
      var bk = sortKey === 'tarih' ? b.tarih+' '+b.saat : b[sortKey]||'';
      if (ak < bk) return sortDir==='asc'?-1:1;
      if (ak > bk) return sortDir==='asc'?1:-1;
      return 0;
    });
  }, [liste, colTarih, colEmail, colKonu, colDurum, sortKey, sortDir]);

  var gonderildi = goruntulenen.filter(function(e){ return e.durum==='Gönderildi'; }).length;
  var basarisiz  = goruntulenen.filter(function(e){ return e.durum==='Başarısız'; }).length;
  var bekliyor   = goruntulenen.filter(function(e){ return e.durum==='Bekliyor'; }).length;

  var durumRenk = {
    'Gönderildi': 'bg-glass-emerald text-emerald-700 border-emerald-200/50',
    'Başarısız':  'bg-glass-red text-red-700 border-red-200/50',
    'Bekliyor':   'bg-glass-amber text-amber-700 border-amber-200/50',
  };
  var durumIkon = { 'Gönderildi':'✅', 'Başarısız':'❌', 'Bekliyor':'⏳' };
  var tipIkon   = {
    'Rezervasyon Onayı':   '📋',
    'Check-in Hatırlatma': '🔔',
    'Check-out Teşekkür':  '🙏',
    'Fatura':              '🧾',
    'Promosyon':           '🎁',
    'Genel Bilgi':         'ℹ️',
  };

  return (
    <div className="p-3 md:p-6 space-y-8 min-h-screen bg-gray-50/50 transform-gpu animate-fade-in">

      {/* Premium Header */}
      <div className="relative overflow-hidden bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-xl p-4 md:p-8 transform-gpu">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-blue-100 rounded-full blur-[80px] opacity-50 will-change-transform"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-indigo-100 rounded-full blur-[80px] opacity-40 will-change-transform"></div>
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="flex items-center gap-6 min-w-0">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-700 rounded-2xl flex-shrink-0 flex items-center justify-center text-white text-3xl shadow-2xl shadow-blue-200 transform-gpu hover:scale-105 transition-transform duration-500">📬</div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tighter truncate">E-posta Günlüğü</h1>
              <p className="text-sm font-bold text-gray-400 mt-1 uppercase tracking-widest truncate">Sistemden giden tüm bildirimlerin tam dökümü</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={excelIndir} className="bg-white/80 backdrop-blur-md border-2 border-gray-100/50 hover:border-gray-800 rounded-2xl px-6 py-3 text-[10px] font-black text-gray-700 transition-all uppercase tracking-widest shadow-sm transform-gpu active:scale-95">Excel</button>
            <button onClick={yazdir}    className="bg-gray-800 hover:bg-black text-white rounded-2xl px-6 py-3 text-[10px] font-black transition-all uppercase tracking-widest shadow-xl shadow-gray-200 transform-gpu active:scale-95">Yazdır</button>
          </div>
        </div>
      </div>

      {/* Akıllı Filtre Paneli */}
      <div className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl p-8 relative z-20 overflow-hidden transform-gpu">
        <div className="absolute top-0 right-10 w-40 h-40 bg-blue-50 rounded-full blur-3xl -mt-20 opacity-40 will-change-transform"></div>
        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse shadow-sm" /> İletişim Filtreleme
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 px-1 tracking-widest">Başlangıç Tarihi</label>
            <DatePicker value={baslar} onChange={function(v){ setBaslar(v); }} maxDate={bitis}/>
          </div>
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 px-1 tracking-widest">Bitiş Tarihi</label>
            <DatePicker value={bitis} onChange={function(v){ setBitis(v); }} minDate={baslar}/>
          </div>
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 px-1 tracking-widest">Gönderim Durumu</label>
            <div className="relative group">
              <select value={durum} onChange={function(e){ setDurum(e.target.value); }}
                className="w-full bg-gray-50/50 border-2 border-transparent group-hover:border-blue-400 focus:bg-white focus:border-blue-500 rounded-2xl px-4 py-3 pr-10 text-sm font-black text-gray-700 appearance-none cursor-pointer transition-all outline-none">
                {DURUMLAR.map(function(d){ return <option key={d}>{d}</option>; })}
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs transition-colors group-hover:text-blue-500">▼</span>
            </div>
          </div>
          <div className="flex gap-2 h-full items-end">
            <button onClick={filtrele} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl py-3.5 text-[11px] font-black uppercase tracking-widest shadow-xl shadow-blue-100 transition-all active:scale-95 transform-gpu">Uygula</button>
            <button onClick={temizle}  className="bg-gray-100/50 hover:bg-gray-200 text-gray-500 rounded-2xl px-5 py-3.5 text-[11px] font-black uppercase tracking-widest transition-all transform-gpu">Sıfırla</button>
          </div>
        </div>
      </div>

      {/* Özet kartlar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10 transform-gpu">
        {[
          { label: 'TOPLAM GÖNDERİM', value: goruntulenen.length, bg: 'bg-gradient-to-br from-blue-50/50 to-blue-100/50', text: 'text-blue-700', icon: '📨', shadow:'shadow-blue-100/50' },
          { label: 'BAŞARIYLA GİTTİ', value: gonderildi, bg: 'bg-gradient-to-br from-emerald-50/50 to-emerald-100/50', text: 'text-emerald-700', icon: '✅', shadow:'shadow-emerald-100/50' },
          { label: 'HATA ALINDI', value: basarisiz, bg: 'bg-gradient-to-br from-red-50/50 to-red-100/50', text: 'text-red-700', icon: '❌', shadow:'shadow-red-100/50' },
          { label: 'SIRADA BEKLİYOR', value: bekliyor, bg: 'bg-gradient-to-br from-amber-50/50 to-amber-100/50', text: 'text-amber-700', icon: '⏳', shadow:'shadow-amber-100/50' },
        ].map(k => (
          <div key={k.label} className={`${k.bg} backdrop-blur-sm rounded-[2rem] p-6 border-2 border-white shadow-xl ${k.shadow} transition-all hover:-translate-y-1 group relative overflow-hidden transform-gpu`}>
            <div className="absolute -right-4 -bottom-4 text-7xl opacity-5 group-hover:scale-125 transition-transform duration-500 will-change-transform">{k.icon}</div>
            <div className={`text-2xl font-black ${k.text} tracking-tighter leading-none`}>{k.value}</div>
            <div className={`text-[9px] font-black ${k.text} opacity-60 uppercase tracking-widest mt-2`}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Yükleniyor / Hata */}
      {yukleniyor && (
        <LoadingSpinner fullPage text="Kayıtlar Sorgulanıyor" color="blue" />
      )}
      {hata && !yukleniyor && (
        <div className="bg-red-50/80 backdrop-blur-md border border-red-200/50 rounded-2xl p-5 text-red-700 text-sm font-bold shadow-lg shadow-red-50 animate-shake transform-gpu">{hata}</div>
      )}

      {/* Tablo Modülleri */}
      {!yukleniyor && !hata && (
        <div className="transform-gpu space-y-6">
          {/* Mobil Görünüm */}
          <div className="md:hidden space-y-3">
            {goruntulenen.length === 0 ? (
              <EmptyState icon="📭" text="Dijital iz bulunamadı" />
            ) : goruntulenen.map(function(e) {
              return (
                <div key={e.id} className="bg-white/80 backdrop-blur-xl border-2 border-white/40 rounded-[2rem] p-5 shadow-xl transition-all hover:-translate-y-1 transform-gpu">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{e.tarih} — {e.saat}</p>
                      <p className="font-black text-gray-900 text-base tracking-tight leading-none">{e.misafir}</p>
                      <p className="text-[11px] font-bold text-blue-500 mt-1.5 opacity-80">{e.email}</p>
                    </div>
                    <span className={'text-[10px] font-black px-3 py-1.5 rounded-full border shadow-sm uppercase tracking-widest transition-colors ' + (durumRenk[e.durum]?.replace('bg-','bg-opacity-10 ') || 'bg-gray-100 text-gray-600')}>
                      {durumIkon[e.durum]} {e.durum}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-100/50">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-white/50 backdrop-blur-md rounded-xl flex items-center justify-center text-lg flex-shrink-0 shadow-sm border border-white group-hover:rotate-12 transition-transform">{tipIkon[e.tip] || '📧'}</div>
                      <div className="min-w-0">
                        <p className="text-xs font-black text-gray-800 truncate leading-tight">{e.konu}</p>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mt-1 opacity-60">{e.tip}</p>
                      </div>
                    </div>
                    <button onClick={function() { setDetay(e); }}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-black px-5 py-2.5 rounded-xl shadow-lg shadow-blue-100 uppercase tracking-widest transition-all active:scale-90 flex-shrink-0 transform-gpu">
                      Detay
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Masaüstü Görünüm */}
          <div className="hidden md:block bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl overflow-hidden relative z-10 transition-all transform-gpu">
            <div className="px-10 py-6 border-b border-gray-100/50 bg-gray-50/30 flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-2 h-8 bg-blue-500 rounded-full shadow-sm shadow-blue-100"></div>
                <div>
                  <p className="text-sm font-black text-gray-900 uppercase tracking-tighter">İletişim Logları</p>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Sistem tarafından işlenen {goruntulenen.length} adet kayıt listelenmiştir.</p>
                </div>
              </div>
              <div className="flex gap-2">
                 <span className="flex items-center gap-1.5 text-[9px] font-black text-gray-400 uppercase tracking-widest bg-white/50 px-3 py-1.5 rounded-full shadow-sm border border-white/40 transform-gpu hover:scale-105 transition-transform cursor-help">⚡ Canlı İzleme Aktif</span>
              </div>
            </div>
            <div className="overflow-x-auto min-h-[400px]">
              <DataTable
                anahtarAlan="id"
                satirlar={goruntulenen}
                bosIkon="📭"
                bosMetin="Hücresel veri bulunamadı"
                kolonlar={[
                  {
                    anahtar: 'tarih', baslik: 'Gönderim tarihi', siralanir: true, filtrelenir: true,
                    deger: e => `${e.tarih} ${e.saat}`,
                    ciz: e => (
                      <>
                        <div className="text-xs font-black text-gray-900 leading-none">{e.tarih}</div>
                        <div className="text-[10px] font-bold text-gray-400 mt-2 tracking-widest">{e.saat}</div>
                      </>
                    ),
                  },
                  {
                    anahtar: 'email', baslik: 'Alıcı bilgisi', siralanir: true, filtrelenir: true,
                    deger: e => `${e.misafir} ${e.email}`,
                    ciz: e => (
                      <>
                        <div className="text-xs font-black text-gray-900 leading-none">{e.misafir}</div>
                        <div className="text-[10px] font-bold text-blue-500 mt-2 opacity-80">{e.email}</div>
                      </>
                    ),
                  },
                  {
                    anahtar: 'konu', baslik: 'E-posta konusu', siralanir: true, filtrelenir: true,
                    ciz: e => (
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-gray-50 rounded-2xl flex items-center justify-center text-xl shadow-sm border border-gray-100 font-emoji flex-shrink-0">
                          {tipIkon[e.tip] || '📧'}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-black text-gray-800 leading-tight truncate max-w-[280px]">{e.konu}</div>
                          <div className="text-[9px] font-black text-gray-400 uppercase mt-1.5 tracking-widest opacity-60">{e.tip}</div>
                        </div>
                      </div>
                    ),
                  },
                  {
                    anahtar: 'durum', baslik: 'Durum', siralanir: true, filtrelenir: true,
                    ciz: e => <Rozet renk={durumRozet(e.durum)}>{e.durum}</Rozet>,
                  },
                  {
                    anahtar: 'aksiyon', baslik: 'Aksiyon', hizala: 'sag',
                    ciz: e => (
                      <button onClick={() => setDetay(e)}
                        className="bg-white border-2 border-gray-100 hover:border-gray-900 hover:bg-gray-900 hover:text-white text-[10px] font-black px-5 py-2.5 rounded-2xl transition-all uppercase tracking-widest active:scale-95">
                        İncele
                      </button>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      )}

      {/* UsageInfo */}
      <UsageInfo 
        color="indigo" 
        description="E-posta Günlüğü, otelinizden misafirlere giden otomatik ve manuel tüm e-postaların iletim durumunu izlemenizi sağlar. CRM performansınızı ve misafir bilgilendirme süreçlerinizi optimize etmek için verileri kullanabilirsiniz. Her işlem anlık olarak şifrelenmiş log yapısında tutulur." 
        scenarios={[
          { title: "İletişim Başarı Analizi", content: "Gönderilen ve Başarısız olan e-posta sayılarını karşılaştırın. Eğer başarısızlık oranı yüksekse, e-posta sunucu (SMTP) ayarlarınızı kontrol etmeniz veya hatalı misafir e-posta adreslerini güncellemeniz gerekebilir. İdeal başarı oranı %98 üzeridir." },
          { title: "Rezervasyon Onay Takibi", content: "Rezervasyon onayı e-postalarının misafirlere ulaşıp ulaşmadığını buradan teyit edebilirsiniz. Misafir e-posta almadığını iddia ediyorsa, iletim saatini ve durumunu buradan kontrol ederek destek sağlayabilirsiniz. Yanlışlıkla spam'e düşen mesajları tespit edin." },
          { title: "Tanıtım ve Pazarlama Etkisi", content: "'Promosyon' tipi seçilerek yapılan gönderimlerin başarısını izleyin. Başarısız gönderimler, veritabanınızdaki 'E-posta Temizliği' ihtiyacını gösteren önemli bir sinyaldir. KVKK uyumlu gönderim için logları baz alın." }
        ]}
      />

      {/* Detay Modal */}
      {detay && <DetayModal kayit={detay} onKapat={function(){ setDetay(null); }}/>}

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes modalIn { from { opacity:0; transform: scale(0.95) translateY(20px); } to { opacity:1; transform: scale(1) translateY(0); } }
        .animate-modal-in { animation: modalIn 0.3s cubic-bezier(.22,1,.36,1) both; }
        .bg-glass-emerald { background: rgba(16, 185, 129, 0.08); }
        .bg-glass-red { background: rgba(239, 68, 68, 0.08); }
        .bg-glass-amber { background: rgba(245, 158, 11, 0.08); }
        .animate-bounce-subtle { animation: bounceSubtle 2s infinite; }
        @keyframes bounceSubtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.05); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.1); }
      `}</style>
    </div>
  );
}
